import { useCallback, useMemo, useState } from 'react';
import {
  BILLING_TRANSACTIONS_FETCH_LIMIT,
  BILLING_TRANSACTIONS_PAGE_SIZE,
} from 'shared/constants/billing';
import { useGetBillingTransactionsQuery } from 'store/api/billingApiSlice';
import {
  BillingTransaction,
  BillingTransactionStatus,
} from 'store/api/apiTypes';
import { useTranslation } from './useTranslation';

const formatDateForSearch = (iso: string): string =>
  new Date(iso).toLocaleDateString('en-GB', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  });

export const useBillingTransactions = () => {
  const { t } = useTranslation();
  const [searchInput, setSearchInput] = useState('');
  const [page, setPage] = useState(1);

  const { data, isLoading, isFetching, error } = useGetBillingTransactionsQuery(
    { limit: BILLING_TRANSACTIONS_FETCH_LIMIT }
  );

  const transactions = useMemo(() => data?.transactions ?? [], [data]);
  const failedCount = transactions.filter(
    txn => txn.status === BillingTransactionStatus.FAILED
  ).length;

  const filteredTransactions = useMemo(() => {
    const query = searchInput.trim().toLowerCase();
    if (!query) return transactions;
    return transactions.filter((txn: BillingTransaction) =>
      [
        formatDateForSearch(txn.date_created),
        txn.subscription,
        txn.payment_method,
        t(txn.status),
        `$${txn.amount.toFixed(2)}`,
      ]
        .join(' ')
        .toLowerCase()
        .includes(query)
    );
  }, [transactions, searchInput, t]);

  const pageCount = Math.max(
    1,
    Math.ceil(filteredTransactions.length / BILLING_TRANSACTIONS_PAGE_SIZE)
  );
  const currentPage = Math.min(page, pageCount);
  const pagedTransactions = filteredTransactions.slice(
    (currentPage - 1) * BILLING_TRANSACTIONS_PAGE_SIZE,
    currentPage * BILLING_TRANSACTIONS_PAGE_SIZE
  );

  const onSearchInputChange = useCallback((value: string) => {
    setSearchInput(value);
    setPage(1);
  }, []);

  return {
    transactions: pagedTransactions,
    totalCount: transactions.length,
    filteredCount: filteredTransactions.length,
    failedCount,
    isLoading,
    isFetching,
    error,
    page: currentPage,
    searchInput,
    onSearchInputChange,
    onPageChange: setPage,
  };
};
